import {MCC_RENDER_ORDER, mccFrameIdFor} from './frames';

/**
 * Page-complexity tier, per the guardrails doc. This is what's stored in
 * the MCC store after evaluation.
 *
 * @see {@link https://webflow.atlassian.net/wiki/spaces/DB/pages/3013574669/Canvas+Performance+Guardrails Canvas Performance Guardrails}
 */
export type MCCGuardrailsPolicyType = 'low' | 'medium' | 'high';

export type MCCRevealButtonTooltip = {
  title: string;
  description: string;
};

type MCCGuardrailsPolicy = {
  /** Upper bound (exclusive) of estimated total DOM nodes for this tier. */
  maxTotalNodes: number;
  /** Number of enabled frames selected for the default reveal order. */
  revealBudget: number;
  /**
   * Tooltip for the reveal-content buttons (frame toolbar and Navigator
   * breakpoints list). Use `null` for tiers with no perf messaging.
   */
  revealButtonTooltip: MCCRevealButtonTooltip | null;
};

/**
 * Reveal-button tooltip for tiers where guardrails hide frame content for
 * performance (medium/high).
 */
const REVEAL_BUTTON_TOOLTIP: MCCRevealButtonTooltip = {
  title: 'Content hidden',
  description: 'Revealing may impact performance',
};

/**
 * Static policy values per complexity tier. Look these up with
 * `getGuardrailPolicy(type)` — only the type lives in state.
 *
 * Thresholds (`maxTotalNodes`) are the guardrails doc's per-page node counts
 * (2k / 5k) scaled by the 7 launch-scope breakpoint frames.
 */
const MCC_GUARDRAILS_POLICIES: Record<
  MCCGuardrailsPolicyType,
  MCCGuardrailsPolicy
> = {
  // Low-complexity pages: all breakpoint contents are shown by default.
  low: {
    maxTotalNodes: 14000,
    revealBudget: MCC_RENDER_ORDER.length,
    revealButtonTooltip: null,
  },
  // Medium-complexity pages: cap the default reveal set.
  medium: {
    maxTotalNodes: 35000,
    revealBudget: 4,
    revealButtonTooltip: REVEAL_BUTTON_TOOLTIP,
  },
  // High-complexity pages: use the smallest default reveal set.
  high: {
    maxTotalNodes: Infinity,
    revealBudget: 1,
    revealButtonTooltip: REVEAL_BUTTON_TOOLTIP,
  },
};

/**
 * Maps an estimated total rendered-node count to a complexity tier.
 * The estimate is one frame's DOM count multiplied by the frame count.
 */
export const getGuardrailPolicyType = (
  estimatedTotalNodeCount: number
): MCCGuardrailsPolicyType => {
  if (estimatedTotalNodeCount < MCC_GUARDRAILS_POLICIES.low.maxTotalNodes)
    return 'low';
  if (estimatedTotalNodeCount < MCC_GUARDRAILS_POLICIES.medium.maxTotalNodes)
    return 'medium';
  return 'high';
};

/**
 * Statically evaluates the policy values for a tier.
 */
export const getGuardrailPolicy = (
  type: MCCGuardrailsPolicyType
): MCCGuardrailsPolicy => MCC_GUARDRAILS_POLICIES[type];

/**
 * Computes the ordered list of frame ids to reveal by default for a given
 * active frame and reveal budget: the active frame plus its nearest
 * neighbors in render order, preferring smaller breakpoints first.
 *
 * @example Entering on `main` with a budget of 4 would return `main, medium, small, tiny`.
 */
export const getDefaultRevealOrder = (
  activeFrameId: string,
  revealBudget: number,
  enabledFrameIds: readonly string[]
): string[] => {
  const enabledFrameIdSet = new Set(enabledFrameIds);
  const orderedFrameIds = MCC_RENDER_ORDER.map(mccFrameIdFor).filter(
    (frameId) => enabledFrameIdSet.has(frameId)
  );
  const activeIndex = orderedFrameIds.indexOf(activeFrameId);

  // Unknown frame id — reveal just the active frame defensively.
  if (activeIndex === -1) return [activeFrameId];

  // Take the active frame and the breakpoints after it in render order
  // (toward mobile) first, then extend toward larger breakpoints if the
  // budget hasn't been exhausted.
  const smallerFirst = orderedFrameIds
    .slice(activeIndex)
    .concat(orderedFrameIds.slice(0, activeIndex).reverse());

  return smallerFirst.slice(0, Math.max(1, revealBudget));
};
